import React, { useState, useMemo } from 'react';
import { Filter, Search, Package, Layers, Users, Archive, X, ChevronsUpDown, ChevronsDownUp, ChevronDown, ChevronUp } from 'lucide-react';

// Extracted components
import FilterSection from './filter/FilterSection';
import MainGroupItem from './filter/MainGroupItem';
import SubGroupItem from './filter/SubGroupItem';
import ManufacturerItem from './filter/ManufacturerItem';
import StatusItem from './filter/StatusItem';
import FilterErrorBoundary from './filter/FilterErrorBoundary';
import FilterSkeleton from './filter/FilterSkeleton';

// Custom hook
import useFilterOptions from '../hooks/useFilterOptions';

/**
 * FilterPanel Component - Equal-Priority Cascading Filters
 * 
 * Professional filtering like Amazon, Google Shopping, Airbnb:
 * - All 4 filter sections treated equally (no hierarchy priority)
 * - Bidirectional cascading - any filter can be starting point
 * - Selected items pinned at top
 * - Zero-count items collapsed by default (not hidden)
 * - Live result counter
 * - Applied filters summary bar with one-click removal
 * - Per-section clear button
 */
const FilterPanelContent = ({ 
  isOpen, 
  onClose, 
  selectedFilters, 
  onFilterChange,
  isDrawer = false 
}) => {
  // Use custom hook for filter data
  const {
    mainGroups,
    subGroups,
    manufacturers,
    isLoading,
    isInitialLoad,
    getStatusCount,
    getSubGroupCountForMainGroup,
    splitByRelevance,
    totalMatchingParts,
    totalAllParts
  } = useFilterOptions(selectedFilters);

  // UI State
  const [filterSearch, setFilterSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    mainGroup: true, subGroup: true, manufacturer: true, status: true
  });
  const [showZeroCount, setShowZeroCount] = useState({
    mainGroups: false, subGroups: false, manufacturers: false
  });
  const [showAll, setShowAll] = useState({
    mainGroups: false, subGroups: false, manufacturers: false
  });

  // Section toggles
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const allExpanded = Object.values(expandedSections).every(v => v);
  const toggleAllSections = () => {
    const newState = !allExpanded;
    setExpandedSections({ mainGroup: newState, subGroup: newState, manufacturer: newState, status: newState });
  };

  // ============================================
  // EQUAL-PRIORITY FILTER HANDLERS
  // No cascading cleanup - all filters are peers
  // ============================================
  
  const handleMainGroupChange = (code) => {
    const newMainGroups = selectedFilters.mainGroups?.includes(code)
      ? selectedFilters.mainGroups.filter(c => c !== code)
      : [...(selectedFilters.mainGroups || []), code];
    
    onFilterChange({ 
      ...selectedFilters, 
      mainGroups: newMainGroups, 
      mainGroup: newMainGroups[0] || ''
    });
  };

  const handleSubGroupChange = (mainGroupCode, subgroupCode) => {
    const compositeKey = `${mainGroupCode}-${subgroupCode}`;
    const newSubGroups = selectedFilters.subGroups?.includes(compositeKey)
      ? selectedFilters.subGroups.filter(c => c !== compositeKey)
      : [...(selectedFilters.subGroups || []), compositeKey];
    onFilterChange({ ...selectedFilters, subGroups: newSubGroups });
  };

  const handleManufacturerChange = (name) => {
    const newManufacturers = selectedFilters.manufacturers?.includes(name)
      ? selectedFilters.manufacturers.filter(m => m !== name)
      : [...(selectedFilters.manufacturers || []), name];
    onFilterChange({ ...selectedFilters, manufacturers: newManufacturers });
  };

  const handleStatusChange = (status) => {
    onFilterChange({ ...selectedFilters, status: selectedFilters.status === status ? '' : status });
  };

  // ============================================
  // SECTION CLEAR HANDLERS
  // ============================================
  
  const clearMainGroups = () => {
    onFilterChange({ ...selectedFilters, mainGroups: [], mainGroup: '' });
  };

  const clearSubGroups = () => {
    onFilterChange({ ...selectedFilters, subGroups: [] });
  };

  const clearManufacturers = () => {
    onFilterChange({ ...selectedFilters, manufacturers: [] });
  };

  const clearStatus = () => {
    onFilterChange({ ...selectedFilters, status: '' });
  };

  const clearAllFilters = () => {
    onFilterChange({ mainGroups: [], mainGroup: '', subGroups: [], status: '', manufacturers: [] });
  };

  // ============================================
  // APPLIED FILTER REMOVAL (from summary bar)
  // ============================================
  
  const removeAppliedFilter = (type, value) => {
    switch (type) {
      case 'mainGroup':
        handleMainGroupChange(value);
        break;
      case 'subGroup':
        const [mg, sg] = value.split('-');
        handleSubGroupChange(mg, sg);
        break;
      case 'manufacturer':
        handleManufacturerChange(value);
        break;
      case 'status':
        handleStatusChange(value);
        break;
      default:
        break;
    }
  };

  // Filter by search
  const filterBySearch = (items, fields) => {
    if (!filterSearch) return items;
    const search = filterSearch.toLowerCase();
    return items.filter(item => fields.some(f => item[f]?.toLowerCase().includes(search)));
  };

  // ============================================
  // PREPARE DATA WITH SELECTED ITEMS PINNED
  // ============================================
  
  const filteredMainGroups = filterBySearch(mainGroups, ['code', 'name']);
  const filteredSubGroups = filterBySearch(subGroups, ['subgroup_code', 'name', 'main_group_code']);
  const filteredManufacturers = filterBySearch(manufacturers, ['name']);

  // Split and sort: selected first, then relevant (count > 0), then zero-count
  const mainGroupData = useMemo(() => {
    return splitByRelevance(filteredMainGroups, selectedFilters.mainGroups || []);
  }, [filteredMainGroups, selectedFilters.mainGroups, splitByRelevance]);

  const subGroupData = useMemo(() => {
    const selectedKeys = selectedFilters.subGroups || [];
    return splitByRelevance(
      filteredSubGroups.map(sg => ({
        ...sg,
        _key: `${sg.main_group_code}-${sg.subgroup_code}`
      })),
      selectedKeys
    );
  }, [filteredSubGroups, selectedFilters.subGroups, splitByRelevance]);

  const manufacturerData = useMemo(() => {
    return splitByRelevance(
      filteredManufacturers.map(m => ({ ...m, _key: m.name })),
      selectedFilters.manufacturers || []
    );
  }, [filteredManufacturers, selectedFilters.manufacturers, splitByRelevance]);

  // Combine selected + relevant for display (selected always shown)
  const displayedMainGroups = useMemo(() => {
    const combined = [...mainGroupData.selected, ...mainGroupData.relevant];
    return showAll.mainGroups ? combined : combined.slice(0, 10);
  }, [mainGroupData, showAll.mainGroups]);

  const displayedSubGroups = useMemo(() => {
    const combined = [...subGroupData.selected, ...subGroupData.relevant];
    return showAll.subGroups ? combined : combined.slice(0, 10);
  }, [subGroupData, showAll.subGroups]);

  const displayedManufacturers = useMemo(() => {
    const combined = [...manufacturerData.selected, ...manufacturerData.relevant];
    return showAll.manufacturers ? combined : combined.slice(0, 10);
  }, [manufacturerData, showAll.manufacturers]);

  const activePartsCount = getStatusCount('active');
  const archivedPartsCount = getStatusCount('deleted');
  
  const activeFilterCount = (selectedFilters.mainGroups?.length || 0) + 
    (selectedFilters.subGroups?.length || 0) + 
    (selectedFilters.status ? 1 : 0) + 
    (selectedFilters.manufacturers?.length || 0);

  const showSkeleton = isInitialLoad && isLoading;

  // ============================================
  // BUILD APPLIED FILTERS FOR SUMMARY BAR
  // ============================================
  
  const appliedFilters = useMemo(() => {
    const chips = [];
    
    // Main groups
    (selectedFilters.mainGroups || []).forEach(code => {
      const group = mainGroups.find(g => g.code === code);
      chips.push({
        type: 'mainGroup',
        value: code,
        label: group ? `${code} - ${group.name}` : code,
        color: 'purple'
      });
    });
    
    // Subgroups
    (selectedFilters.subGroups || []).forEach(key => {
      const [mg, sg] = key.split('-');
      const subgroup = subGroups.find(s => s.main_group_code === mg && s.subgroup_code === sg);
      chips.push({
        type: 'subGroup',
        value: key,
        label: subgroup ? `${mg}${sg} - ${subgroup.name}` : key,
        color: 'orange'
      });
    });
    
    // Manufacturers
    (selectedFilters.manufacturers || []).forEach(name => {
      chips.push({
        type: 'manufacturer',
        value: name,
        label: name,
        color: 'blue'
      });
    });
    
    // Status
    if (selectedFilters.status) {
      chips.push({
        type: 'status',
        value: selectedFilters.status,
        label: selectedFilters.status === 'active' ? 'Active' : 'Archived',
        color: selectedFilters.status === 'active' ? 'green' : 'red'
      });
    }
    
    return chips;
  }, [selectedFilters, mainGroups, subGroups]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  // Zero-count toggle button (collapsed by default)
  const renderZeroCountToggle = (key, count) => count > 0 && (
    <button
      onClick={() => setShowZeroCount(prev => ({ ...prev, [key]: !prev[key] }))}
      className="w-full text-left text-xs text-slate-400 hover:text-slate-600 py-2 flex items-center space-x-1"
      data-testid={`show-zero-count-${key}`}
    >
      {showZeroCount[key] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      <span>{showZeroCount[key] ? 'Hide' : 'Show'} {count} with no results</span>
    </button>
  );

  // Show more button
  const renderShowMoreButton = (key, totalCount) => totalCount > 10 && (
    <button
      onClick={() => setShowAll(prev => ({ ...prev, [key]: !prev[key] }))}
      className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-2"
      data-testid={`show-all-${key}`}
    >
      {showAll[key] ? '− Show less' : `+ Show all ${totalCount}`}
    </button>
  );

  // Color classes for chips
  const chipColors = {
    purple: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
    green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
    red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
  };

  const panelContent = (
    <div className="h-full flex flex-col">
      {/* Header - Only show in drawer mode, not when inside tabs */}
      {isDrawer && (
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-800">Filters</h3>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {activeFilterCount}
              </span>
            )}
            {isLoading && !isInitialLoad && (
              <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={toggleAllSections} 
              className="p-1.5 hover:bg-slate-200 rounded transition-colors" 
              title={allExpanded ? 'Collapse All' : 'Expand All'} 
              data-testid="toggle-all-sections-btn"
            >
              {allExpanded ? <ChevronsDownUp className="w-4 h-4 text-slate-500" /> : <ChevronsUpDown className="w-4 h-4 text-slate-500" />}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
      )}

      {/* Compact sub-header when NOT in drawer mode (inside tabs) - Matches Activity tab style */}
      {!isDrawer && (
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0">
          <div className="flex items-center gap-2 text-xs">
            {/* Parts count badge - similar to Activity's "20 events" badge */}
            <span className="px-2 py-0.5 bg-haden-primary/10 text-haden-primary font-medium rounded-full">
              {totalMatchingParts} of {totalAllParts} parts
            </span>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-medium rounded-full">
                {activeFilterCount} active
              </span>
            )}
            {isLoading && !isInitialLoad && (
              <span className="w-3 h-3 border-2 border-haden-primary border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          <button 
            onClick={toggleAllSections} 
            className="p-1.5 hover:bg-slate-100 rounded transition-colors" 
            title={allExpanded ? 'Collapse All' : 'Expand All'} 
            data-testid="toggle-all-sections-btn"
          >
            {allExpanded ? <ChevronsDownUp className="w-4 h-4 text-slate-400" /> : <ChevronsUpDown className="w-4 h-4 text-slate-400" />}
          </button>
        </div>
      )}

      {/* Applied Filters Summary Bar */}
      {appliedFilters.length > 0 && (
        <div className="p-3 border-b border-slate-200 bg-slate-50/50 flex-shrink-0" data-testid="applied-filters-bar">
          <div className="flex flex-wrap gap-1.5">
            {appliedFilters.map((chip, idx) => (
              <span
                key={`${chip.type}-${chip.value}-${idx}`}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-colors ${chipColors[chip.color]}`}
              >
                <span className="max-w-[120px] truncate">{chip.label}</span>
                <button
                  onClick={() => removeAppliedFilter(chip.type, chip.value)}
                  className="ml-1 p-0.5 rounded-full hover:bg-black/10 transition-colors"
                  data-testid={`remove-filter-${chip.type}-${chip.value}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs text-slate-500 hover:text-red-600 underline px-1"
              data-testid="clear-all-applied"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Live Result Counter - Only show in drawer mode, otherwise it's in sub-header */}
      {isDrawer && (
        <div className="px-4 py-2 border-b border-slate-100 bg-white flex-shrink-0" data-testid="live-result-counter">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{totalMatchingParts.toLocaleString()}</span>
            <span className="text-slate-400"> of </span>
            <span className="text-slate-500">{totalAllParts.toLocaleString()}</span>
            <span className="text-slate-400"> parts</span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="p-3 border-b border-slate-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search filters..." 
            value={filterSearch} 
            onChange={(e) => setFilterSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
            data-testid="filter-search-input" 
          />
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto overscroll-contain filter-panel-scroll">
        {/* Main Group */}
        <FilterSection 
          title="Main Group" 
          icon={Package} 
          iconColor="text-purple-500" 
          expanded={expandedSections.mainGroup} 
          onToggle={() => toggleSection('mainGroup')} 
          badge={selectedFilters.mainGroups?.length || 0}
          onClear={clearMainGroups}
          testId="filter-section-maingroup"
        >
          {showSkeleton ? <FilterSkeleton rows={5} /> : (
            <>
              {displayedMainGroups.map(g => (
                <MainGroupItem 
                  key={g.code} 
                  group={g} 
                  isSelected={selectedFilters.mainGroups?.includes(g.code)} 
                  isZeroCount={g.count === 0} 
                  subGroupCount={getSubGroupCountForMainGroup(g.code)} 
                  onToggle={handleMainGroupChange} 
                />
              ))}
              {renderShowMoreButton('mainGroups', mainGroupData.selected.length + mainGroupData.relevant.length)}
              {renderZeroCountToggle('mainGroups', mainGroupData.zeroCount.length)}
              {showZeroCount.mainGroups && (
                <div className="space-y-1 border-t border-slate-100 pt-2">
                  {mainGroupData.zeroCount.map(g => (
                    <MainGroupItem 
                      key={g.code} 
                      group={g} 
                      isSelected={selectedFilters.mainGroups?.includes(g.code)} 
                      isZeroCount={true} 
                      subGroupCount={getSubGroupCountForMainGroup(g.code)} 
                      onToggle={handleMainGroupChange} 
                    />
                  ))}
                </div>
              )}
              {filteredMainGroups.length === 0 && (
                <p className="text-sm text-slate-400 italic p-2">No main groups found</p>
              )}
            </>
          )}
        </FilterSection>

        {/* Sub Group */}
        <FilterSection 
          title="Sub Group" 
          icon={Layers} 
          iconColor="text-orange-500" 
          expanded={expandedSections.subGroup} 
          onToggle={() => toggleSection('subGroup')} 
          badge={selectedFilters.subGroups?.length || 0}
          onClear={clearSubGroups}
          testId="filter-section-subgroup"
        >
          {showSkeleton ? <FilterSkeleton rows={5} /> : (
            <>
              {displayedSubGroups.map(sg => (
                <SubGroupItem 
                  key={`${sg.main_group_code}-${sg.subgroup_code}`} 
                  subgroup={sg} 
                  isSelected={selectedFilters.subGroups?.includes(`${sg.main_group_code}-${sg.subgroup_code}`)} 
                  isZeroCount={sg.count === 0} 
                  onToggle={handleSubGroupChange} 
                />
              ))}
              {renderShowMoreButton('subGroups', subGroupData.selected.length + subGroupData.relevant.length)}
              {renderZeroCountToggle('subGroups', subGroupData.zeroCount.length)}
              {showZeroCount.subGroups && (
                <div className="space-y-1 border-t border-slate-100 pt-2">
                  {subGroupData.zeroCount.map(sg => (
                    <SubGroupItem 
                      key={`${sg.main_group_code}-${sg.subgroup_code}`} 
                      subgroup={sg} 
                      isSelected={selectedFilters.subGroups?.includes(`${sg.main_group_code}-${sg.subgroup_code}`)} 
                      isZeroCount={true} 
                      onToggle={handleSubGroupChange} 
                    />
                  ))}
                </div>
              )}
              {filteredSubGroups.length === 0 && (
                <p className="text-sm text-slate-400 italic p-2">No subgroups found</p>
              )}
            </>
          )}
        </FilterSection>

        {/* Manufacturer */}
        <FilterSection 
          title="Manufacturer" 
          icon={Users} 
          iconColor="text-blue-500" 
          expanded={expandedSections.manufacturer} 
          onToggle={() => toggleSection('manufacturer')} 
          badge={selectedFilters.manufacturers?.length || 0}
          onClear={clearManufacturers}
          testId="filter-section-manufacturer"
        >
          {showSkeleton ? <FilterSkeleton rows={5} /> : (
            <>
              {displayedManufacturers.map(m => (
                <ManufacturerItem 
                  key={m.name} 
                  manufacturer={m} 
                  isSelected={selectedFilters.manufacturers?.includes(m.name)} 
                  isZeroCount={m.count === 0} 
                  onToggle={handleManufacturerChange} 
                />
              ))}
              {renderShowMoreButton('manufacturers', manufacturerData.selected.length + manufacturerData.relevant.length)}
              {renderZeroCountToggle('manufacturers', manufacturerData.zeroCount.length)}
              {showZeroCount.manufacturers && (
                <div className="space-y-1 border-t border-slate-100 pt-2">
                  {manufacturerData.zeroCount.map(m => (
                    <ManufacturerItem 
                      key={m.name} 
                      manufacturer={m} 
                      isSelected={selectedFilters.manufacturers?.includes(m.name)} 
                      isZeroCount={true} 
                      onToggle={handleManufacturerChange} 
                    />
                  ))}
                </div>
              )}
              {filteredManufacturers.length === 0 && (
                <p className="text-sm text-slate-400 italic p-2">No manufacturers found</p>
              )}
            </>
          )}
        </FilterSection>

        {/* Status */}
        <FilterSection 
          title="Status" 
          icon={Archive} 
          iconColor="text-slate-500" 
          expanded={expandedSections.status} 
          onToggle={() => toggleSection('status')} 
          badge={selectedFilters.status ? 1 : 0}
          onClear={clearStatus}
          testId="filter-section-status"
        >
          {showSkeleton ? <FilterSkeleton rows={2} /> : (
            <>
              <StatusItem 
                status="active" 
                label="Active" 
                count={activePartsCount} 
                isSelected={selectedFilters.status === 'active'} 
                isZeroCount={activePartsCount === 0} 
                onToggle={handleStatusChange} 
                colorClass="green" 
              />
              <StatusItem 
                status="deleted" 
                label="Archived" 
                count={archivedPartsCount} 
                isSelected={selectedFilters.status === 'deleted'} 
                isZeroCount={archivedPartsCount === 0} 
                onToggle={handleStatusChange} 
                colorClass="red" 
              />
            </>
          )}
        </FilterSection>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
        <button 
          onClick={clearAllFilters} 
          disabled={activeFilterCount === 0}
          className="w-full py-2 px-4 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
          data-testid="clear-all-filters-btn"
        >
          Clear All Filters
        </button>
        {isDrawer && (
          <button 
            onClick={onClose} 
            className="w-full mt-2 py-2 px-4 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors" 
            data-testid="apply-filters-btn"
          >
            Apply Filters
          </button>
        )}
      </div>
    </div>
  );

  if (isDrawer) {
    return (
      <>
        {isOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={onClose} />}
        <div className={`fixed top-0 right-0 h-full w-[350px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {panelContent}
        </div>
      </>
    );
  }

  // When inside tabs, don't add extra borders/rounded corners - parent handles it
  return (
    <div className="w-full h-full bg-white overflow-hidden flex flex-col">
      {panelContent}
    </div>
  );
};

/**
 * FilterPanel with Error Boundary
 */
const FilterPanel = (props) => {
  const [retryKey, setRetryKey] = useState(0);
  return (
    <FilterErrorBoundary onRetry={() => setRetryKey(prev => prev + 1)}>
      <FilterPanelContent key={retryKey} {...props} />
    </FilterErrorBoundary>
  );
};

export default FilterPanel;
